import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useProcurementUser } from "../context/ProcurementUserContext";
import ProcurementUserSwitcher from "../components/ProcurementUserSwitcher";

const API = process.env.REACT_APP_API_URL || "";
const API_KEY = process.env.REACT_APP_API_KEY || "";

const C = {
  bg: "#0F1117", card: "#161C2E", cardBorder: "#222A40",
  gold: "#C9972B", text: "#F4F0E8", textSec: "#9EA8C0", textDim: "#5C657A",
  red: "#EF4444", green: "#22C55E", amber: "#F59E0B", blue: "#3B82F6",
  teal: "#14B8A6", orange: "#F97316", violet: "#8B5CF6", emerald: "#10B981",
  rule: "#2A3048",
};

const STATUS_COLORS = {
  not_started: "#64748B", in_production: C.blue, ready_to_ship: C.amber,
  shipped: C.violet, in_storage: C.orange, delivered: C.teal,
  installed: C.emerald, commissioned: C.green, ops_turnover: C.gold,
};
const ALL_STATUSES = Object.keys(STATUS_COLORS);
const PAGE_SIZE = 50;

const today = new Date(); today.setHours(0,0,0,0);
const fmtCur = (v) => v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "—";

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: "monospace", background: `${color}22`, color, border: `1px solid ${color}44` }}>{text?.replace(/_/g, " ")}</span>;
}
function Toast({ msg, onClose }) {
  if (!msg) return null;
  return <div style={{ position: "fixed", bottom: 24, right: 24, background: C.green, color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontFamily: "monospace", zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>{msg}<button onClick={onClose} style={{ marginLeft: 12, background: "none", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}>x</button></div>;
}

export default function ProductionPlanner() {
  const { activeUser } = useProcurementUser();
  const hdrs = { "Content-Type": "application/json", ...(API_KEY ? { "X-API-Key": API_KEY } : {}), "X-User": activeUser.identifier };

  const [items, setItems] = useState([]);
  const [viewMode, setViewMode] = useState("internal");
  const [filters, setFilters] = useState({ allocation: "", vendor: "", status: "", equip: "", serial: "" });
  const [sortCol, setSortCol] = useState("allocation");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [editItem, setEditItem] = useState(null);
  const [reallocItem, setReallocItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Edit modal state
  const [eStatus, setEStatus] = useState(""); const [eShipPlan, setEShipPlan] = useState(""); const [eShipActual, setEShipActual] = useState("");
  const [eSerial, setESerial] = useState(""); const [eVendorStorage, setEVendorStorage] = useState(false); const [eStorAddr, setEStorAddr] = useState("");
  const [eOutStor, setEOutStor] = useState(""); const [eVendorPm, setEVendorPm] = useState(""); const [eErr, setEErr] = useState(null);

  // Reallocation modal state
  const [rAlloc, setRAlloc] = useState(""); const [rReason, setRReason] = useState(""); const [rErr, setRErr] = useState(null);

  const fetchItems = useCallback(() => {
    setLoading(true); setError(null);
    const params = filters.allocation ? `?allocation=${encodeURIComponent(filters.allocation)}` : "";
    fetch(`${API}/procurement/planner${params}`, { headers: hdrs })
      .then(r => r.json()).then(d => { setItems(d.planner_items || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [activeUser, filters.allocation]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openEdit = (item) => {
    setEditItem(item); setEStatus(item.production_status || "not_started"); setEShipPlan(item.mfg_ship_date_plan || "");
    setEShipActual(item.mfg_ship_date_actual || ""); setESerial(item.serial_number || ""); setEVendorStorage(item.vendor_storage || false);
    setEStorAddr(item.vendor_storage_address || ""); setEOutStor(item.out_of_storage_vendor || ""); setEVendorPm(item.vendor_pm || ""); setEErr(null);
  };

  const submitEdit = async () => {
    setEErr(null);
    const body = {};
    if (eStatus && eStatus !== editItem.production_status) body.production_status = eStatus;
    if (eShipPlan) body.mfg_ship_date_plan = eShipPlan;
    if (eShipActual) body.mfg_ship_date_actual = eShipActual;
    if (eSerial) body.serial_number = eSerial;
    if (eVendorStorage !== editItem.vendor_storage) body.vendor_storage = eVendorStorage;
    if (eStorAddr) body.vendor_storage_address = eStorAddr;
    if (eOutStor) body.out_of_storage_vendor = eOutStor;
    if (eVendorPm) body.vendor_pm = eVendorPm;
    if (Object.keys(body).length === 0) { setEditItem(null); return; }
    try {
      const r = await fetch(`${API}/procurement/planner/${editItem.id}`, { method: "PATCH", headers: hdrs, body: JSON.stringify(body) });
      if (!r.ok) { const d = await r.json(); setEErr(d.detail || "Error"); return; }
      setEditItem(null); setToast(`Saved — ${editItem.equipment_type} updated`); fetchItems(); setTimeout(() => setToast(null), 3000);
    } catch (e) { setEErr(e.message); }
  };

  const openRealloc = (item) => { setReallocItem(item); setRAlloc(""); setRReason(""); setRErr(null); };

  const submitRealloc = async () => {
    if (!rAlloc.trim() || rReason.trim().length < 10) { setRErr("Allocation required + reason min 10 chars"); return; }
    setRErr(null);
    try {
      const r = await fetch(`${API}/procurement/planner/${reallocItem.id}/reallocate`, { method: "POST", headers: hdrs, body: JSON.stringify({ new_allocation: rAlloc.trim(), reason: rReason.trim() }) });
      if (!r.ok) { const d = await r.json(); setRErr(d.detail || "Error"); return; }
      setReallocItem(null); setToast(`Reallocated → ${rAlloc.trim()}`); fetchItems(); setTimeout(() => setToast(null), 3000);
    } catch (e) { setRErr(e.message); }
  };

  const exportCsv = () => {
    if (!items.length) return;
    const cols = viewMode === "vendor"
      ? ["allocation","build_phase_seq","equipment_type","vendor_name","sku","unit_num","serial_number","contractual_date","requested_delivery","mfg_ship_date_plan","mfg_ship_date_actual","on_time_performance","delivery_first_dest","production_status"]
      : ["allocation","build_phase_seq","equipment_type","vendor_name","sku","unit_num","serial_number","contractual_date","requested_delivery","mfg_ship_date_plan","mfg_ship_date_actual","on_time_performance","delivery_first_dest","unit_price","production_status"];
    const csv = [cols.join(","), ...filteredItems.map(r => cols.map(c => `"${(r[c] ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `planner_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const handleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } setPage(0); };

  const vendorNames = useMemo(() => [...new Set(items.map(r => r.vendor_name).filter(Boolean))].sort(), [items]);

  const filteredItems = useMemo(() => {
    let rows = items;
    if (filters.vendor) rows = rows.filter(r => r.vendor_name === filters.vendor);
    if (filters.status) rows = rows.filter(r => r.production_status === filters.status);
    if (filters.equip) rows = rows.filter(r => (r.equipment_type || "").toLowerCase().includes(filters.equip.toLowerCase()));
    if (filters.serial) rows = rows.filter(r => (r.serial_number || "").toLowerCase().includes(filters.serial.toLowerCase()));
    return rows;
  }, [items, filters]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === "string" && typeof bv === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av - bv) : (bv - av);
    });
  }, [filteredItems, sortCol, sortDir]);

  const pagedItems = useMemo(() => sortedItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [sortedItems, page]);
  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE);

  const stats = useMemo(() => ({
    total: filteredItems.length,
    withSerial: filteredItems.filter(r => r.serial_number).length,
    onTime: filteredItems.filter(r => r.on_time_performance === true).length,
    pastDue: filteredItems.filter(r => r.contractual_date && new Date(r.contractual_date) < today).length,
  }), [filteredItems]);

  const isVendorView = viewMode === "vendor";
  const si = (col) => sortCol === col ? (sortDir === "asc" ? " \u2191" : " \u2193") : " \u2195";
  const th = { padding: "8px 10px", fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, cursor: "pointer", whiteSpace: "nowrap", borderBottom: `1px solid ${C.rule}`, textAlign: "left", letterSpacing: "0.06em" };
  const td = { padding: "6px 8px", fontSize: 12, borderBottom: `1px solid ${C.rule}`, fontFamily: "monospace" };
  const inp = { background: C.bg, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "monospace", outline: "none" };
  const sel = { ...inp, cursor: "pointer" };
  const btn = { background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace" };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1600, margin: "0 auto", padding: "16px 24px 48px" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, borderBottom: `1px solid ${C.rule}`, paddingBottom: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Production Planner</h1>
            <div style={{ fontSize: 12, color: C.textSec }}>{items.length} items | {vendorNames.length} vendors</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => setViewMode(v => v === "internal" ? "vendor" : "internal")} style={{ ...btn, background: isVendorView ? C.amber : C.card, color: isVendorView ? C.bg : C.textSec, border: isVendorView ? "none" : `1px solid ${C.cardBorder}` }}>
              {isVendorView ? "Vendor Preview" : "Internal View"}
            </button>
            <ProcurementUserSwitcher />
            <button onClick={fetchItems} style={{ ...btn, background: C.card, color: C.textSec, border: `1px solid ${C.cardBorder}` }}>Refresh</button>
            <button onClick={exportCsv} disabled={!items.length} style={{ ...btn, opacity: items.length ? 1 : 0.4 }}>Export CSV</button>
          </div>
        </div>

        {/* VENDOR PREVIEW BANNER */}
        {isVendorView && (
          <div style={{ background: `${C.amber}11`, border: `1px solid ${C.amber}33`, borderRadius: 8, padding: "8px 16px", marginBottom: 12, fontSize: 12, color: C.amber }}>
            Vendor Preview — showing only columns visible to vendors. Financial data and reallocation controls are hidden.
          </div>
        )}

        {/* FILTER BAR */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
          <input value={filters.allocation} onChange={e => { setFilters(f => ({ ...f, allocation: e.target.value })); setPage(0); }} placeholder="Allocation" style={{ ...inp, width: 180 }} />
          <select value={filters.vendor} onChange={e => { setFilters(f => ({ ...f, vendor: e.target.value })); setPage(0); }} style={sel}>
            <option value="">All Vendors</option>
            {vendorNames.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0); }} style={sel}>
            <option value="">All Statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <input value={filters.equip} onChange={e => { setFilters(f => ({ ...f, equip: e.target.value })); setPage(0); }} placeholder="Equipment Type" style={{ ...inp, width: 140 }} />
          <input value={filters.serial} onChange={e => { setFilters(f => ({ ...f, serial: e.target.value })); setPage(0); }} placeholder="Serial #" style={{ ...inp, width: 130 }} />
          <button onClick={() => { setFilters({ allocation: "", vendor: "", status: "", equip: "", serial: "" }); setPage(0); }} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Clear</button>
        </div>

        {loading && <div style={{ color: C.textDim, fontStyle: "italic", padding: 40, textAlign: "center" }}>Loading...</div>}
        {error && <div style={{ color: C.red, padding: 12, fontSize: 13 }}>{error}</div>}

        {/* EMPTY STATE */}
        {!loading && filteredItems.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0", color: C.textDim }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#x1F4E6;</div>
            <div style={{ fontSize: 15 }}>No planner items found</div>
            <div style={{ fontSize: 12, marginTop: 6, marginBottom: 16 }}>Adjust filters or run Oracle ingest to populate data</div>
            <a href="/procurement" style={{ color: C.gold, textDecoration: "none", fontSize: 13 }}>&larr; Back to Dashboard</a>
          </div>
        )}

        {/* STATS + TABLE */}
        {!loading && filteredItems.length > 0 && (
          <>
            {/* STATS ROW */}
            <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
              {[
                { label: "Total Items", value: stats.total, color: C.text },
                { label: "With Serial #", value: stats.withSerial, color: C.green },
                { label: "On Time", value: stats.onTime, color: C.green },
                { label: "Past Due", value: stats.pastDue, color: stats.pastDue > 0 ? C.red : C.green },
              ].map((s, i) => (
                <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "10px 16px", minWidth: 120 }}>
                  <div style={{ fontSize: 10, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
                </div>
              ))}
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: C.textDim, fontFamily: "monospace", alignSelf: "center" }}>{sortedItems.length} rows | Page {page + 1}/{totalPages || 1}</span>
            </div>

            {/* TABLE */}
            <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${C.cardBorder}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: C.card }}>
                <thead>
                  <tr>
                    <th style={th}>Status</th>
                    <th style={th} onClick={() => handleSort("allocation")}>Alloc{si("allocation")}</th>
                    <th style={th} onClick={() => handleSort("build_phase_seq")}>Phase{si("build_phase_seq")}</th>
                    <th style={th} onClick={() => handleSort("equipment_type")}>Equip{si("equipment_type")}</th>
                    <th style={th} onClick={() => handleSort("vendor_name")}>Vendor{si("vendor_name")}</th>
                    <th style={th}>SKU</th>
                    <th style={th}>Unit</th>
                    <th style={th}>Serial #</th>
                    <th style={th} onClick={() => handleSort("contractual_date")}>Contract{si("contractual_date")}</th>
                    <th style={th}>Req. Del</th>
                    <th style={th}>MFG Plan</th>
                    <th style={th}>MFG Actual</th>
                    <th style={th}>On Time</th>
                    <th style={th}>Del. Dest</th>
                    {!isVendorView && <th style={{ ...th, textAlign: "right" }}>Unit Price</th>}
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((r, i) => {
                    const sc = STATUS_COLORS[r.production_status] || "#64748B";
                    const isPastDue = r.contractual_date && new Date(r.contractual_date) < today;
                    const lateShip = r.mfg_ship_date_actual && r.mfg_ship_date_plan && r.mfg_ship_date_actual > r.mfg_ship_date_plan;
                    return (
                      <tr key={r.id || i} style={{ borderLeft: `3px solid ${sc}`, background: isPastDue ? `${C.red}06` : "transparent" }}>
                        <td style={td}><Badge text={r.production_status} color={sc} /></td>
                        <td style={{ ...td, color: C.amber }}>{r.allocation}</td>
                        <td style={td}>{r.build_phase_seq || "—"}</td>
                        <td style={td}>{r.equipment_type || "—"}</td>
                        <td style={{ ...td, color: C.textSec }}>{r.vendor_name || "—"}</td>
                        <td style={td}>{r.sku || "—"}</td>
                        <td style={td}>{r.unit_num && r.total_units_on_line ? `${r.unit_num} of ${r.total_units_on_line}` : r.unit_num || "—"}</td>
                        <td style={{ ...td, color: r.serial_number ? C.amber : C.textDim }}>{r.serial_number || "—"}</td>
                        <td style={{ ...td, color: isPastDue ? C.red : C.text }}>{r.contractual_date || "—"}</td>
                        <td style={td}>{r.requested_delivery || "—"}</td>
                        <td style={td}>{r.mfg_ship_date_plan || "—"}</td>
                        <td style={{ ...td, background: lateShip ? `${C.orange}15` : "transparent" }}><span style={{ color: lateShip ? C.orange : C.text }}>{r.mfg_ship_date_actual || "—"}</span></td>
                        <td style={td}>{r.on_time_performance === true ? <span style={{ color: C.green }}>{"\u2713"}</span> : r.on_time_performance === false ? <span style={{ color: C.red }}>{"\u2717"}</span> : "—"}</td>
                        <td style={{ ...td, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.delivery_first_dest}>{r.delivery_first_dest || "—"}</td>
                        {!isVendorView && <td style={{ ...td, textAlign: "right" }}>{fmtCur(r.unit_price)}</td>}
                        <td style={td}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button onClick={() => openEdit(r)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 4, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontFamily: "monospace" }}>Edit</button>
                            {!isVendorView && <button onClick={() => openRealloc(r)} style={{ background: "none", border: `1px solid ${C.amber}`, color: C.amber, borderRadius: 4, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontFamily: "monospace" }}>Realloc</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>&laquo; Prev</button>
                <span style={{ color: C.textDim, fontSize: 12, padding: "4px 8px", fontFamily: "monospace" }}>{page + 1} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>Next &raquo;</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* EDIT MODAL */}
      {editItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setEditItem(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 440, maxWidth: "90vw", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Edit — {editItem.equipment_type} Unit {editItem.unit_num}</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, fontFamily: "monospace" }}>{editItem.serial_number || `ID #${editItem.id}`}</div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Production Status</label><select value={eStatus} onChange={e => setEStatus(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>{ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
            {[["MFG Ship Plan", eShipPlan, setEShipPlan, "date"], ["MFG Ship Actual", eShipActual, setEShipActual, "date"], ["Serial Number", eSerial, setESerial, "text"], ["Vendor PM", eVendorPm, setEVendorPm, "text"]].map(([l, v, s, t]) => (
              <div key={l} style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>{l}</label><input type={t} value={v} onChange={e => s(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            ))}
            <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={eVendorStorage} onChange={e => setEVendorStorage(e.target.checked)} id="vstor" />
              <label htmlFor="vstor" style={{ fontSize: 12, color: C.textSec }}>Vendor Storage</label>
            </div>
            {eVendorStorage && (
              <>
                <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Storage Address</label><input value={eStorAddr} onChange={e => setEStorAddr(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
                <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Out of Storage Date</label><input type="date" value={eOutStor} onChange={e => setEOutStor(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
              </>
            )}
            {eErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{eErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setEditItem(null)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitEdit} style={btn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* REALLOCATION MODAL */}
      {reallocItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setReallocItem(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 420, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Reallocate</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, fontFamily: "monospace" }}>{reallocItem.equipment_type} {reallocItem.serial_number || `#${reallocItem.id}`} | Current: {reallocItem.allocation}</div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>New Allocation *</label><input value={rAlloc} onChange={e => setRAlloc(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Reason * (min 10 chars)</label><textarea value={rReason} onChange={e => setRReason(e.target.value)} rows={3} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} /></div>
            {rErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{rErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setReallocItem(null)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={submitRealloc} style={{ ...btn, background: C.amber }}>Reallocate</button>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast} onClose={() => setToast(null)} />
    </div>
  );
}
